import React from 'react';
import { Product } from '../types/types';
import { ProductCard } from './ProductCard';
import { useLanguage } from '../context/LanguageContext';
interface MenuSectionProps {
  title: string;
  products: Product[];
  onAddProduct: (product: Product) => void;
}
const SUBCATEGORY_INFO: Record<string, {
  emoji: string;
  tagline: string;
}> = {
  // Pizza subcategories
  Clásicas: {
    emoji: '🍕',
    tagline: 'Los sabores de siempre'
  },
  'De Carnes': {
    emoji: '🥩',
    tagline: 'Para los amantes de la carne'
  },
  Especialidades: {
    emoji: '⭐',
    tagline: 'Creaciones únicas del chef'
  },
  'De Mar': {
    emoji: '🦐',
    tagline: 'Frescura del océano'
  },
  'Calzones y Stromboli': {
    emoji: '🥟',
    tagline: 'Masa rellena horneada'
  },
  Siciliana: {
    emoji: '👑',
    tagline: 'Nuestra pizza cuadrada especial'
  },
  'Por Porción': {
    emoji: '✂️',
    tagline: 'Pedí solo lo que querés'
  },
  // Bebidas subcategories
  Calientes: {
    emoji: '☕',
    tagline: 'Para calentar el alma'
  },
  Frappé: {
    emoji: '🧊',
    tagline: 'Helados y cremosos'
  },
  'Batidos Premium': {
    emoji: '🥤',
    tagline: 'Combinaciones irresistibles'
  },
  Milkshakes: {
    emoji: '🍦',
    tagline: 'Cremosos y refrescantes'
  },
  'Cerveza Cruda': {
    emoji: '🍺',
    tagline: 'Recién servida'
  },
  Refrescos: {
    emoji: '🧃',
    tagline: 'Naturales del día'
  },
  'Gaseosas y Tropicales': {
    emoji: '🫧',
    tagline: ''
  },
  'Cervezas y Licores': {
    emoji: '🍻',
    tagline: ''
  }
};
// Define the display order for pizza subcategories
const PIZZA_SUBCATEGORY_ORDER = ['Clásicas', 'De Carnes', 'Especialidades', 'De Mar', 'Calzones y Stromboli', 'Siciliana', 'Por Porción'];
function SubcategoryHeader({
  subcategory
}: {
  subcategory: string;
}) {
  const info = SUBCATEGORY_INFO[subcategory];
  const emoji = info?.emoji || '🍽️';
  const tagline = info?.tagline || '';
  return <div className="flex items-center gap-3 mb-4 mt-2" data-id="element-1299">
      <div className="w-10 h-10 rounded-xl bg-surface-light flex items-center justify-center text-lg flex-shrink-0" data-id="element-1300">
        {emoji}
      </div>
      <div className="flex-1 min-w-0" data-id="element-1301">
        <h3 className="text-base font-heading font-semibold text-white leading-tight" data-id="element-1302">
          {subcategory}
        </h3>
        {tagline && <p className="text-xs text-gray-500 mt-0.5 leading-snug" data-id="element-1303">{tagline}</p>}
      </div>
      <div className="h-px flex-1 bg-gray-800/60 max-w-[60px]" data-id="element-1304" />
    </div>;
}
export function MenuSection({
  title,
  products,
  onAddProduct
}: MenuSectionProps) {
  const {
    translateCategory,
    translateSubcategory
  } = useLanguage();
  if (products.length === 0) return null;
  // Check if products have subcategories
  const hasSubcategories = products.some(p => p.subcategory);
  if (hasSubcategories) {
    // Group by subcategory
    const groups: Record<string, Product[]> = {};
    products.forEach(p => {
      const sub = p.subcategory || 'Otros';
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(p);
    });
    // Sort subcategories: use PIZZA_SUBCATEGORY_ORDER if it's Pizzas, otherwise keep original order
    const sortedEntries = Object.entries(groups).sort(([a], [b]) => {
      const aIdx = PIZZA_SUBCATEGORY_ORDER.indexOf(a);
      const bIdx = PIZZA_SUBCATEGORY_ORDER.indexOf(b);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    });
    return <div className="mb-8" data-id="element-1305">
        <h2 className="text-xl font-heading font-bold text-white mb-5 px-1 tracking-wide" data-id="element-1306">
          {translateCategory(title)}
        </h2>
        {sortedEntries.map(([subcategory, subProducts], index) => <div key={subcategory} className={index > 0 ? 'mt-6' : ''} data-id="element-1307">
            <SubcategoryHeader subcategory={translateSubcategory(subcategory) || subcategory} data-id="element-1308" />
            <div className="grid grid-cols-2 gap-3" data-id="element-1309">
              {subProducts.map(product => <ProductCard key={product.id} product={product} onAdd={onAddProduct} data-id="element-1310" />)}
            </div>
            {index < sortedEntries.length - 1 && <div className="mt-6 border-t border-gray-800/40" data-id="element-1311" />}
          </div>)}
      </div>;
  }
  return <div className="mb-8" data-id="element-1312">
      <h2 className="text-xl font-heading font-bold text-white mb-4 px-1 tracking-wide" data-id="element-1313">
        {translateCategory(title)}
      </h2>
      <div className="grid grid-cols-2 gap-3" data-id="element-1314">
        {products.map(product => <ProductCard key={product.id} product={product} onAdd={onAddProduct} data-id="element-1315" />)}
      </div>
    </div>;
}